import { type FC, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import type { VariantL10n } from '../../services/data-review';
import { ProductVariantCard } from './ProductVariantCard';
import { markMultipleAsReviewed } from '../../utils/reviewed-products';

interface ProductFamilyGroupProps {
  familyId: number;
  familyName: string;
  variants: VariantL10n[];
  locale: string;
  onMarkAsReviewed?: () => void;
}

const EXCLUDED_FIELDS = new Set(['sku', 'familyId', 'variantId', 'imageUrl', 'createdAt', 'updatedAt', 'previewUrl', 'locale']);

const analyzeFieldDifferences = (variants: VariantL10n[]): Set<string> => {
  if (variants.length <= 1) {
    return new Set();
  }

  const differentFields = new Set<string>();
  const firstVariant = variants.at(0)!;

  for (const key of Object.keys(firstVariant) as Array<keyof VariantL10n>) {
    if (EXCLUDED_FIELDS.has(key)) {
      continue;
    }

    const firstValue = firstVariant[key];
    const hasDifference = variants.some((variant) => {
      const currentValue = variant[key];
      return String(currentValue ?? '') !== String(firstValue ?? '');
    });

    if (hasDifference) {
      differentFields.add(key);
    }
  }

  return differentFields;
};

export const ProductFamilyGroup: FC<ProductFamilyGroupProps> = ({
  familyId,
  familyName,
  variants,
  locale,
  onMarkAsReviewed,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(false);

  const handleMarkFamilyAsReviewed = useCallback(() => {
    const variantIds = variants.map((v) => v.variantId);
    markMultipleAsReviewed(variantIds, locale);
    onMarkAsReviewed?.();
  }, [variants, locale, onMarkAsReviewed]);

  const differentFields = useMemo(() => analyzeFieldDifferences(variants), [variants]);

  // Check if content is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
        const isScrollable = scrollWidth > clientWidth;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
        setShowScrollFade(isScrollable && !isAtEnd);
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener('scroll', checkScrollable);

    return () => {
      window.removeEventListener('resize', checkScrollable);
      scrollContainer?.removeEventListener('scroll', checkScrollable);
    };
  }, [variants]);

  // Get first variant's preview URL if available
  const previewUrl = variants.at(0)?.previewUrl;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {/* Family Header */}
      <div className="mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{familyName}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Family ID: {familyId} • {variants.length} variant{variants.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 text-blue-600 hover:text-blue-800 text-sm whitespace-nowrap transition-colors"
              >
                View Product →
              </a>
            )}
            <button
              onClick={handleMarkFamilyAsReviewed}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              ✓ Mark Family as Reviewed
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Scroll Container for Variants */}
      <div className="relative">
        <div ref={scrollContainerRef} className="overflow-x-auto -mx-4 px-4">
          <div className="flex gap-4 pb-2" style={{ minWidth: 'min-content' }}>
            {variants.map((variant) => (
              <div
                key={`${variant.variantId}-${variant.locale}`}
                className="flex-shrink-0"
                style={{ width: '600px' }}
              >
                <ProductVariantCard
                  variant={variant}
                  locale={locale}
                  differentFields={differentFields}
                />
              </div>
            ))}
          </div>
        </div>
        {/* Scroll fade indicator */}
        {showScrollFade && (
          <div className="absolute right-0 top-0 bottom-0 w-20 pointer-events-none bg-gradient-to-l from-gray-900/20 to-transparent" />
        )}
      </div>
    </div>
  );
};
